import React, { CSSProperties, forwardRef, MouseEvent, useCallback } from 'react';
import cn from 'classnames';
import { Icon, IconColor, IconName, IconProps } from '@ets/design-system';

import styles from './styles.module.scss';

export type SplitToggleProps = {
  /** If the popover is open */
  isOpen: boolean;
  /** If the SplitToggle is toggled */
  isToggled: boolean;
  /** The handler when the action are is clicked */
  onClick: (e: MouseEvent<HTMLDivElement>) => void;
  /** The name of the Icon 24x24 to render on the left side */
  icon?: IconName;
  /** A custom icon component to use instead of icon name */
  customIcon?: React.ReactNode;
  /** Sets the color of the Icon */
  iconColor?: IconColor;
  /** If the SplitToggle is disabled */
  disabled?: boolean;
  /** onClick handler for the popper toggle */
  onPopperClick: (e: MouseEvent<HTMLDivElement>) => void;
  /** Set the amount of margin in the tag */
  margin?: CSSProperties['margin'];
  /** ID to be used in unit or automated tests.\
   * Will result in the following different attributes:
   * * ${testId}-container
   * * ${testId}-action-toggle
   * * ${testId}-action-toggle-icon
   * * ${testId}-popper-toggle
   * * ${testId}-popper-toggle-icon
   * * ${testId}-popover
   */
  testId: string;
  custom?: IconProps['custom'];
  style?: IconProps['style'];
};

export const SplitToggle = forwardRef<HTMLDivElement, SplitToggleProps>(
  (
    {
      isToggled,
      onClick,
      icon,
      customIcon,
      iconColor,
      onPopperClick,
      disabled = false,
      margin = '0',
      testId,
      isOpen,
      custom,
      style,
    },
    ref,
  ) => {
    // TEMPORARY: This is a temporary workaround to use the custom icon for the highlighter,
    // since the icon color only on the line is not supported for the Underline and Strikethrough icons.
    // This will be removed after an improvement to create Underline and Strikethrough custom icons
    const shouldUseCustomIcon = icon === 'highlighter' && !!customIcon;

    const handleOnClick = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        if (!disabled) {
          onClick(e);
        }
      },
      [onClick, disabled],
    );
    const handleOnPopperClick = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        if (!disabled) {
          onPopperClick(e);
        }
      },
      [disabled, onPopperClick],
    );
    return (
      <div
        className={cn(styles.root, {
          [styles.isToggled]: isToggled,
          [styles.isOpen]: isOpen,
          [styles.isDisabled]: disabled,
        })}
        ref={ref}
        style={{ margin }}
        tabIndex={0}
        data-testid={`${testId}-container`}
        data-disabled={disabled}
      >
        <div
          className={cn(styles.leftArea, { [styles.isToggled]: isToggled })}
          onClick={handleOnClick}
          data-testid={`${testId}-action-toggle`}
        >
          {shouldUseCustomIcon ? (
            customIcon
          ) : icon ? (
            <Icon
              size="3"
              custom={custom}
              icon={icon}
              color={iconColor}
              testId={`${testId}-action`}
              style={style}
            />
          ) : (
            customIcon
          )}
        </div>
        <div
          className={cn(styles.rightArea, { [styles.isOpen]: isOpen })}
          onClickCapture={handleOnPopperClick}
          data-testid={`${testId}-popper-toggle`}
        >
          <Icon size="2" icon="chevron-down" testId={`${testId}-popper`} color="neutral" />
        </div>
      </div>
    );
  },
);
